import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ShieldCheckIcon, GlobeIcon, CalendarIcon } from 'lucide-react';
import { Language } from '../App';
interface HeroProps {
  language: Language;
  scrollToForm: () => void;
}
const content = {
  en: {
    headline: 'Private Medical Care Tailored to You',
    sub: 'Licensed physicians. Personalized consultations. Complete discretion.',
    cta: 'Book a Consultation',
    badges: ['Licensed', 'English & Spanish', 'By Appointment Only']
  },
  es: {
    headline: 'Atención Médica Privada Hecha a Su Medida',
    sub: 'Médicos con licencia. Consultas personalizadas. Discreción total.',
    cta: 'Reservar Consulta',
    badges: ['Licenciado', 'Inglés y Español', 'Solo con Cita']
  }
};
export function Hero({
  language,
  scrollToForm
}: HeroProps) {
  const t = content[language];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <section className="min-h-screen flex items-center pt-20 px-4 sm:px-6 lg:px-8" data-id="element-97">
      <div className="max-w-7xl mx-auto w-full" data-id="element-98">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center" data-id="element-99">
          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="lg:col-span-3 space-y-8" data-id="element-100">
            <motion.h2 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-pearl leading-tight" data-id="element-101">
              {t.headline}
            </motion.h2>

            <motion.p variants={itemVariants} className="text-lg sm:text-xl text-pearl/80 leading-relaxed max-w-2xl" data-id="element-102">
              {t.sub}
            </motion.p>

            <motion.button variants={itemVariants} onClick={scrollToForm} className="bg-gradient-to-r from-rose-400 to-rose-500 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-2xl hover:shadow-rose-500/50 transition-all duration-300 hover:scale-105" data-id="element-103">
              {t.cta}
            </motion.button>

            <motion.div variants={containerVariants} className="flex flex-wrap gap-3 pt-4" data-id="element-104">
              {t.badges.map((badge, index) => <motion.div key={badge} variants={itemVariants} className="flex items-center gap-2 bg-pearl/10 border border-pearl/20 rounded-full px-4 py-2" data-id="element-105">
                  {index === 0 && <ShieldCheckIcon className="w-4 h-4 text-pearl" data-id="element-106" />}
                  {index === 1 && <GlobeIcon className="w-4 h-4 text-pearl" data-id="element-107" />}
                  {index === 2 && <CalendarIcon className="w-4 h-4 text-pearl" data-id="element-108" />}
                  <span className="text-pearl text-sm font-medium" data-id="element-109">
                    {badge}
                  </span>
                </motion.div>)}
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.5
        }} className="lg:col-span-2" data-id="element-110">
            <div className="relative" data-id="element-111">
              <div className="absolute inset-0 bg-gradient-to-br from-pearl/20 to-transparent rounded-3xl blur-2xl" data-id="element-112" />
              <div className="relative bg-neutral-900 rounded-3xl overflow-hidden shadow-2xl ring-1 ring-pearl/20" data-id="element-113">
                <img src="https://cdn.magicpatterns.com/uploads/sUFK5tKBD2PKf3yJhJKKqK/Screenshot_2026-04-09_at_1.49.08_PM.png" alt="Pearl Ann Brown Medical Practice" className="w-full h-auto" data-id="element-114" />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-espresso/90 to-transparent p-6" data-id="element-115">
                  <p className="text-pearl text-sm font-medium" data-id="element-116">
                    {language === 'en' ? 'Private Consultation Room' : 'Sala de Consulta Privada'}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}